// Page PUBLIQUE d'acceptation d'une invitation d'équipe.
//
// Rendue AVANT la vérification de session (voir `App.tsx`) : la personne
// invitée n'a pas encore de compte. Elle voit qui l'invite et dans quel projet,
// choisit son nom et son mot de passe, et arrive CONNECTÉE dans le projet.
// L'adresse e-mail est celle de l'invitation : elle ne se choisit pas ici.
import { LegalFooter } from '../legal/LegalPages';
import { TermsConsent } from '../legal/TermsConsent';
import { useEffect, useState, type FormEvent } from 'react';
import { useAcceptTeamInvitation, usePublicTeamInvitation } from '../api/queries';
import { ApiRequestError } from '../api/client';
import { Button, ErrorBanner, Eyebrow, Field, Spinner } from '../components/ui';
import { LogoMark, Wordmark } from '../components/Logo';
import { useRouter } from '../router';
import '../auth/auth.css';

const MIN_PASSWORD = 8;

export function AcceptInvitationPage({ token }: { token: string }) {
  const inv = usePublicTeamInvitation(token);
  const accept = useAcceptTeamInvitation(token);
  const { navigate } = useRouter();
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);

  useEffect(() => {
    if (inv.data?.name) setName(inv.data.name);
  }, [inv.data]);

  // Le jeton est dans l'URL : il ne doit pas partir en Referer vers un tiers.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      const r = await accept.mutateAsync({ name: name.trim(), password, acceptTerms });
      // `replace` : revenir en arrière ne doit pas rouvrir un lien consommé.
      navigate(`/projet/${encodeURIComponent(r.projectId)}`, { replace: true });
    } catch {
      /* rendu par le bandeau */
    }
  }

  if (inv.isPending) {
    return (
      <div className="app-loading">
        <Spinner label="Vérification de l’invitation…" />
      </div>
    );
  }

  return (
    <main className="auth">
      <div className="auth-card">
        <div className="auth-brand">
          <LogoMark size={32} />
          <Wordmark className="auth-brand-name" />
        </div>

        {inv.isError ? (
          <>
            <h1 className="t-h2">Invitation plus valide</h1>
            <p className="auth-lede">
              {inv.error instanceof ApiRequestError && inv.error.status === 404
                ? 'Elle a peut-être expiré, été annulée ou déjà utilisée. Demandez un nouveau lien à la personne qui vous l’a envoyé.'
                : 'Le serveur est momentanément injoignable. Réessayez dans un instant.'}
            </p>
          </>
        ) : (
          <>
            <Eyebrow>Invitation · {inv.data.organizationName}</Eyebrow>
            <h1 className="t-h2">Rejoindre « {inv.data.projectName} »</h1>
            <p className="auth-lede">
              Vous êtes invité·e en tant que <strong>{inv.data.role}</strong>. Choisissez votre nom et
              votre mot de passe pour accéder au projet.
            </p>
            <form className="auth-form" onSubmit={submit} noValidate>
              <Field label="E-mail" type="email" value={inv.data.email} readOnly disabled />
              <Field label="Votre nom" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
              <Field
                label="Mot de passe"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                hint={`Au moins ${MIN_PASSWORD} caractères.`}
                {...(password.length > 0 && password.length < MIN_PASSWORD ? { error: `Encore ${MIN_PASSWORD - password.length} caractère(s).` } : {})}
              />
              <TermsConsent checked={acceptTerms} onChange={setAcceptTerms} />
              {accept.error ? (
                <ErrorBanner>{accept.error instanceof ApiRequestError ? accept.error.message : 'La création du compte a échoué.'}</ErrorBanner>
              ) : null}
              <Button type="submit" variant="accent" loading={accept.isPending} disabled={!name.trim() || password.length < MIN_PASSWORD || !acceptTerms}>
                Créer mon accès
              </Button>
            </form>
          </>
        )}
        <LegalFooter compact />
      </div>
    </main>
  );
}
